/**
 * product-kits
 *
 * Seletor de kits da página de produto. Portado do tema Glozin. Faz duas coisas:
 *
 * 1. Alimenta o formulário do tema (snippets/product-form.liquid) com a
 *    quantidade do kit escolhido. Quem envia ao carrinho continua sendo o custom
 *    element `product-form` do tema, que monta o payload a partir do próprio
 *    form — por isso basta existir um campo `quantity` dentro dele.
 *
 *    Há dois cenários, e os dois precisam funcionar:
 *      - Seletor de quantidade ligado: o campo já existe dentro de
 *        <m-quantity-input>. Atualizamos o valor dele.
 *      - Seletor desligado: não existe campo `quantity` nenhum. Injetamos um
 *        hidden. Sem isso o cliente escolhe "3 unidades" e vai 1 para o carrinho.
 *    Nunca os dois ao mesmo tempo: dois campos com name="quantity" fazem o
 *    FormData enviar valores duplicados.
 *
 * 2. Recalcula os preços quando o cliente troca de variante. O tema atualiza a
 *    página trocando só alguns elementos por id (product-info.js,
 *    handleUpdateProductInfo: Price, Inventory, Sku, Availability, Volume,
 *    PricePerItem). O bloco de kits não está nessa lista — o que é bom, porque
 *    preserva o kit que o cliente escolheu, mas significa que os preços,
 *    calculados em Liquid, congelariam no preço da primeira variante. Em produto
 *    de variante única isso nunca aparece; em produto com variantes de preços
 *    diferentes, o cliente veria o preço errado.
 *
 * A troca de variante é detectada relendo o campo `id` do formulário após
 * qualquer mudança na página — é o mesmo campo que product-info.js atualiza em
 * updateVariantInputs().
 */
if (!customElements.get('product-kits')) {
  class ProductKits extends HTMLElement {
    connectedCallback() {
      this.onChange = this.onChange.bind(this);
      this.onDocumentChange = this.onDocumentChange.bind(this);
      this.onPageShow = this.onPageShow.bind(this);

      this.options = Array.from(this.querySelectorAll('[data-kit-option]'));
      this.inputs = this.options
        .map((option) => option.querySelector('.dg-kit__radio'))
        .filter(Boolean);

      this.variants = this.readVariants();
      this.currentVariantId = null;

      this.addEventListener('change', this.onChange);
      document.addEventListener('change', this.onDocumentChange);
      window.addEventListener('pageshow', this.onPageShow);

      // setQuantityBoundries também roda depois de cada atualização de carrinho
      // (product-info.js, initQuantityHandlers), fora de qualquer evento change.
      // Esse pub/sub é o único aviso que o tema emite nesse caminho.
      const events = window.MinimogEvents;
      const topic = window.MinimogTheme && window.MinimogTheme.pubSubEvents
        ? window.MinimogTheme.pubSubEvents.quantityUpdate
        : null;
      if (events && topic && typeof events.subscribe === 'function') {
        this.unsubscribeQuantity = events.subscribe(topic, () => {
          requestAnimationFrame(() => this.sync());
        });
      }

      this.ensureSelection();
      this.markSelection();

      // O bloco pode ser avaliado antes do formulário existir no DOM (a ordem dos
      // blocos é o lojista quem decide). Adiar para o próximo frame garante que
      // um kit pré-selecionado maior que 1 chegue ao formulário mesmo que o
      // cliente não toque em nada.
      requestAnimationFrame(() => {
        this.refreshPrices();
        this.sync();
      });
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('change', this.onDocumentChange);
      window.removeEventListener('pageshow', this.onPageShow);
      if (typeof this.unsubscribeQuantity === 'function') this.unsubscribeQuantity();
    }

    readVariants() {
      const script = this.querySelector('[data-kits-variants]');
      if (!script) return {};
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return {};
      }
    }

    get form() {
      return document.getElementById(this.dataset.formId);
    }

    /**
     * Todos os formulários que levam este produto ao carrinho.
     *
     * O botão de compra fixo (snippets/sticky-atc.liquid) tem form próprio, com
     * o seu próprio seletor de quantidade. Em getFormData (theme-global.js) o
     * tema só copia do form principal os campos que faltam no sticky
     * (`if (!formData.has(key))`) — e `quantity` nunca falta, porque o sticky
     * já traz o seu. Sem sincronizar os dois, o cliente escolhe "3 unidades",
     * compra pelo botão fixo e leva 1.
     */
    get syncTargets() {
      const forms = [];
      const main = this.form;
      if (main) forms.push(main);

      document.querySelectorAll('sticky-atc form').forEach((form) => {
        if (!forms.includes(form)) forms.push(form);
      });

      return forms;
    }

    get selected() {
      return this.inputs.find((input) => input.checked && !input.disabled);
    }

    /**
     * Garante que sempre haja um kit marcado. O lojista pode não marcar nenhum,
     * marcar um que ficou sem estoque, ou remover o que estava marcado.
     */
    ensureSelection() {
      if (this.selected) return;
      const first = this.inputs.find((input) => !input.disabled);
      if (first) first.checked = true;
    }

    onChange(event) {
      if (!event.target.classList.contains('dg-kit__radio')) return;
      this.markSelection();
      this.sync();
    }

    /**
     * Qualquer mudança fora do bloco pode ser uma troca de variante. Reler o
     * campo `id` do formulário é mais confiável do que tentar adivinhar qual
     * seletor de variante o tema está usando (o Brigattini tem dropdown, swatch,
     * botão e imagem, cada um com marcação própria).
     */
    onDocumentChange(event) {
      if (this.contains(event.target)) return;
      requestAnimationFrame(() => {
        this.refreshPrices();
        // Reescrever a quantidade também, e não só os preços: ao trocar de
        // variante o tema chama setQuantityBoundries (theme-global.js), que faz
        // `input.value = min` — devolvendo o campo a 1. Como atribuir .value não
        // emite change, nada avisava o bloco, e o cliente com "Kit 3 unidades"
        // na tela levava 1 ao carrinho.
        this.sync();
      });
    }

    /**
     * Ao voltar pelo histórico, o navegador restaura o radio que o cliente
     * tinha marcado — só que isso acontece DEPOIS de o script já ter posto a
     * classe .is-selected no kit que veio marcado do servidor. Como o destaque
     * é pintado por duas regras independentes (.is-selected e :has(:checked)),
     * os dois cartões apareciam selecionados ao mesmo tempo.
     *
     * A restauração não dispara `change`, então não há evento para escutar
     * além deste. Vale tanto para o bfcache quanto para a restauração comum de
     * formulário, e rodar de novo num carregamento normal é inofensivo.
     */
    onPageShow() {
      requestAnimationFrame(() => {
        this.markSelection();
        this.sync();
      });
    }

    /**
     * Espelho em classe do estado :checked. O CSS usa :has() para o destaque,
     * que não existe em navegador antigo — sem isto o cliente não enxergaria
     * qual kit está selecionado.
     */
    markSelection() {
      this.options.forEach((option) => {
        const input = option.querySelector('.dg-kit__radio');
        option.classList.toggle('is-selected', Boolean(input && input.checked));
      });
    }

    fill(template, value) {
      if (!template) return String(value);
      return template.replace('__VALUE__', value);
    }

    /**
     * O Brigattini expõe formatMoney(cents, format) global em theme-global.js —
     * é a mesma função que product-card-swatch.js usa para reescrever preço.
     * O fallback cobre a ordem de carregamento: se o bloco rodar antes do
     * theme-global, o preço sai sem símbolo em vez de sair "NaN".
     */
    get moneyFormat() {
      // theme-data.liquid publica aqui o formato ja resolvido por
      // settings.currency_code_enabled — a mesma fonte que product-card-swatch.js
      // e product-bundles.js usam. Ler shop.money_format cru fazia o kit exibir
      // a moeda num formato e o resto da loja em outro.
      return (window.MinimogSettings && window.MinimogSettings.money_format) || '';
    }

    formatMoney(cents) {
      if (typeof window.formatMoney === 'function') {
        return window.formatMoney(cents, this.moneyFormat);
      }
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, this.moneyFormat);
      }
      return (cents / 100).toFixed(2);
    }

    /**
     * Recalcula os preços de todos os kits para a variante selecionada. A conta
     * é a mesma do Liquid em snippets/product-kit-option.liquid — as duas
     * precisam andar juntas, senão a página mostra um preço no carregamento e
     * outro depois de trocar a variante.
     */
    refreshPrices() {
      const form = this.form;
      if (!form) return;

      const field = form.querySelector('[name="id"]');
      if (!field) return;

      const variant = this.variants[field.value];
      if (!variant || field.value === this.currentVariantId) return;
      this.currentVariantId = field.value;

      this.options.forEach((option) => {
        const qty = parseInt(option.dataset.kitQuantity, 10) || 1;
        // parseFloat, e não parseInt: o Liquid aceita desconto fracionário
        // ("16.5" sai 16.5 do filtro `plus: 0`). Com parseInt o JS truncava para
        // 16 e o preço mudava sozinho assim que o cliente trocava de variante.
        const discount = parseFloat(option.dataset.kitDiscount) || 0;

        // A disponibilidade vinha assada do Liquid, calculada só para a variante
        // inicial: trocar de variante mantinha habilitado um kit maior que o
        // estoque novo, e o erro só aparecia ao clicar em comprar.
        const max = typeof variant.max === 'number' ? variant.max : -1;
        const disponivel = variant.available && (max < 0 || max >= qty);
        const radio = option.querySelector('.dg-kit__radio');
        if (radio) radio.disabled = !disponivel;
        option.classList.toggle('dg-kit--unavailable', !disponivel);

        const full = variant.price * qty;
        let compare = full;
        if (qty === 1 && variant.compare > variant.price) {
          compare = variant.compare;
        }

        // Mesma regra do Liquid: valor fixo em centavos ganha do percentual.
        const amount = parseFloat(option.dataset.kitAmount) || 0;
        const total = amount > 0
          ? Math.max(0, full - amount)
          : (discount > 0 ? Math.round((full * (100 - discount)) / 100) : full);
        const unit = Math.round(total / qty);

        // Mesma regra do Liquid: o percentual vem da diferença entre os preços
        // que estão na tela, não do desconto configurado no painel.
        let off = 0;
        if (compare > total) {
          off = Math.round(((compare - total) * 100) / compare);
        }

        const totalEl = option.querySelector('[data-kit-total]');
        if (totalEl) totalEl.textContent = this.formatMoney(total);

        const compareWrap = option.querySelector('[data-kit-compare]');
        if (compareWrap) {
          compareWrap.hidden = compare <= total;
          const compareEl = compareWrap.querySelector('[data-kit-compare-value]');
          if (compareEl) compareEl.textContent = this.formatMoney(compare);
        }

        // Os textos são remontados a partir do template, trocando o marcador.
        // Editar o texto já formatado duplicava o símbolo da moeda.
        const unitEl = option.querySelector('[data-kit-unit]');
        if (unitEl) {
          unitEl.hidden = qty <= 1;
          if (qty > 1) {
            unitEl.textContent = this.fill(unitEl.dataset.kitUnitTemplate, this.formatMoney(unit));
          }
        }

        const offEl = option.querySelector('[data-kit-off]');
        if (offEl) {
          offEl.hidden = off <= 0;
          if (off > 0) offEl.textContent = this.fill(offEl.dataset.kitOffTemplate, off);
        }
      });

      // O kit escolhido pode ter acabado de ser desabilitado pela regra acima.
      this.ensureSelection();
      this.markSelection();
    }

    sync() {
      const input = this.selected;
      if (!input) return;

      const quantity = parseInt(input.value, 10) || 1;
      this.syncTargets.forEach((form) => this.syncForm(form, quantity));
    }

    syncForm(form, quantity) {
      const visible = form.querySelector('m-quantity-input input[name="quantity"]');
      const hidden = form.querySelector('input[data-kits-quantity]');

      if (visible) {
        // Com o seletor de quantidade na tela, ele é a fonte da verdade: o hidden
        // sobraria como segundo campo `quantity` e duplicaria o valor no payload.
        if (hidden) hidden.remove();

        if (visible.value !== String(quantity)) {
          visible.value = quantity;
          visible.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      const field = hidden || document.createElement('input');
      if (!hidden) {
        field.type = 'hidden';
        field.name = 'quantity';
        field.setAttribute('data-kits-quantity', '');
        form.appendChild(field);
      }
      field.value = quantity;
    }
  }

  customElements.define('product-kits', ProductKits);
}
